import { Popover } from "@headlessui/react"
import classNames from "classnames"
import React, { useContext } from "react"
import { useMarina } from "src/contexts/MarinaContext"
import { formatDate } from "src/main/Billing/Items/helpers"
import PaymentMethodDescription from "src/main/Billing/Payments/PaymentMethodDescription"
import PaymentStatusBadge from "src/main/Billing/Payments/PaymentStatusBadge"

import Button from "src/components/Button"
import Chips from "src/components/Chips"
import DataTable from "src/components/DataTable"
import Form from "src/components/Form"
import OverflowMenu from "src/components/OverflowMenu"

import { useTracker } from "src/hooks/use_tracker"

import { formatCurrencyFromMinorUnits } from "src/utils/UnitConversion"

import { ContactInvoicesContext } from "./ContactInvoicesContextProvider"
import { getDateFilterText } from "./helpers"

const ContactInvoicesTable = () => {
  const tracker = useTracker()
  const { marinaSlug, presentmentCurrency } = useMarina()

  const {
    sortConfig,
    statusFilters,
    statusOptions,
    handleFilterSelect,
    startDate,
    endDate,
    handleStartDateChange,
    handleEndDateChange,
    handleClearFilters,
    isError,
    invoices,
    isFetching,
    printViewEnabled,
  } = useContext(ContactInvoicesContext)

  const colDefs = [
    {
      key: "invoice_id",
      header: "Invoice",
      render: (invoice) =>
        invoice.displayAsBlankRow ? (
          <div />
        ) : (
          <div className="truncate">{invoice.id}</div>
        ),
      width: "20%",
    },
    {
      key: "status",
      header: "Status",
      render: (invoice) =>
        invoice.displayAsBlankRow ? (
          <div />
        ) : (
          <PaymentStatusBadge payment={invoice} />
        ),
    },
    {
      key: "due_date",
      header: "Due Date",
      render: (invoice) =>
        invoice.displayAsBlankRow ? (
          <div />
        ) : invoice.dueDate ? (
          formatDate(invoice.dueDate)
        ) : (
          "-"
        ),
      sortable: true,
    },
    {
      key: "total_amount",
      header: "Total",
      render: (invoice) =>
        invoice.displayAsBlankRow ? (
          <div />
        ) : (
          formatCurrencyFromMinorUnits({
            amount: invoice.totalAmount,
            currency: presentmentCurrency,
          })
        ),
      sortable: true,
    },
    {
      key: "balance",
      header: "Balance",
      render: (invoice) =>
        invoice.displayAsBlankRow ? (
          <div />
        ) : (
          formatCurrencyFromMinorUnits({
            amount: invoice.balance,
            currency: presentmentCurrency,
          })
        ),
      sortable: true,
    },
    {
      key: "method",
      header: "Method",
      render: (invoice) =>
        invoice.displayAsBlankRow ? (
          <div />
        ) : (
          <PaymentMethodDescription paymentMethod={invoice.paymentMethod} />
        ),
      width: "16%",
    },
    {
      key: "source",
      header: "Source",
      render: (invoice) => {
        if (invoice.displayAsBlankRow) return <div />

        const sourceTypeDisplay =
          invoice.sourceType === "Reservation"
            ? "Reservation"
            : invoice.sourceType
        return (
          <div>
            <span className="text-gray-900">#{invoice.sourceId}</span>
            <div className="text-xs text-gray-600">{sourceTypeDisplay}</div>
          </div>
        )
      },
    },
  ]

  const openViewInvoicePath = (invoice) => {
    tracker.trackEvent("customer_invoices_tab:view_invoice_pressed")
    window.open(`/manage/${marinaSlug}/payments/${invoice.id}/view`, "_blank")
  }

  const rowConfig = {
    actions: (invoice) => {
      if (invoice.displayAsBlankRow) {
        return null
      }
      return [
        {
          action: "View Invoice",
          onClick: () => openViewInvoicePath(invoice),
        },
        {
          action: `View ${invoice.sourceType}`,
          onClick: () => {
            tracker.trackEvent("customer_invoices_tab:view_source_pressed")
            window.open(invoice.sourcePath, "_blank")
          },
        },
      ]
    },
    onClick: (invoice) => {
      if (invoice.displayAsBlankRow) {
        return null
      }
      return openViewInvoicePath(invoice)
    },
  }

  const renderFilterChips = () => {
    return statusFilters.map((status) => {
      const label =
        statusOptions.find((opt) => opt.value === status)?.label || status
      return (
        <Chips.Chip
          key={`status-${status}`}
          text={label}
          onClick={() => handleFilterSelect(statusFilters, status)}
          data-testid={`status-filter-chip-${status}`}
        />
      )
    })
  }

  const renderDateChip = () => {
    if (!startDate && !endDate) return null

    return (
      <Chips.Chip
        text={getDateFilterText(startDate, endDate)}
        onClick={() => {
          handleStartDateChange()
          handleEndDateChange()
        }}
      />
    )
  }

  const renderChips = () => {
    const hasFilters = statusFilters.length > 0 || startDate || endDate

    if (!hasFilters) return null

    return (
      <div className="mb-4">
        <Chips onReset={handleClearFilters}>
          {renderFilterChips()}
          {renderDateChip()}
        </Chips>
      </div>
    )
  }

  const renderStatusFilterDropdown = () => (
    <OverflowMenu menuButtonLabel="Status">
      <div className="space-y-2 p-4">
        {statusOptions.map((option) => (
          <Form.Checkbox
            key={option.value}
            name={`status-${option.value}`}
            label={option.label}
            checked={statusFilters.includes(option.value)}
            onChange={() => handleFilterSelect(statusFilters, option.value)}
          />
        ))}
      </div>
    </OverflowMenu>
  )

  const renderDateFilterDropdown = () => (
    <Popover as="div" className="relative w-full">
      {({ open }) => (
        <>
          <Popover.Button
            className={`btn-secondary flex items-center whitespace-nowrap focus:outline-none`}
          >
            <span>Due Date</span>
            <i
              className={classNames("icon ml-2 text-xxxs", {
                "icon-sf-chevron-down": !open,
                "icon-sf-chevron-up": open,
              })}
            />
          </Popover.Button>
          <Popover.Panel className="absolute left-0 z-10 w-48 origin-top-left border bg-white p-4 shadow-lg">
            <div className="space-y-2">
              <div className="flex flex-col">
                <Form.Label htmlFor="start-date">Start Date</Form.Label>
                <Form.DatePicker
                  id="start-date"
                  value={startDate}
                  onChange={handleStartDateChange}
                  maxDate={endDate}
                  dateFormat="MM/dd/yyyy"
                  todayButton="Today"
                />
              </div>
              <div className="flex flex-col">
                <Form.Label htmlFor="end-date">End Date</Form.Label>
                <Form.DatePicker
                  id="end-date"
                  value={endDate}
                  onChange={handleEndDateChange}
                  minDate={startDate}
                  dateFormat="MM/dd/yyyy"
                  todayButton="Today"
                />
              </div>
            </div>
          </Popover.Panel>
        </>
      )}
    </Popover>
  )

  const renderEmptyState = () => (
    <div className="card flex h-96 flex-col items-center justify-center">
      <span className="text-lg font-semibold">No matching data</span>
      <span className="text-gray-600">
        Try adjusting your filters to view more
      </span>
    </div>
  )

  const renderErrorState = () => (
    <div className="card flex h-96 flex-col items-center justify-center">
      <span className="text-lg font-semibold text-red-600">
        Error loading data
      </span>
      <span className="text-gray-600">
        Please try refreshing the page or contact support at mayday@dockwa.com
        if the issue persists
      </span>
    </div>
  )

  const handleExportSummaryClick = () => {
    tracker.trackEvent("customer_invoices_tab:export_summary_pressed")
    window.print()
  }

  const renderExportSummaryButton = () => {
    return (
      <Button onClick={handleExportSummaryClick} variant="secondary">
        Export Summary
      </Button>
    )
  }

  return (
    <div data-testid="contact-invoices-table">
      <div className="mb-4 flex w-full items-center justify-between">
        <div className="flex space-x-1">
          {renderStatusFilterDropdown()}
          {renderDateFilterDropdown()}
        </div>
        {printViewEnabled ? renderExportSummaryButton() : null}
      </div>

      {renderChips()}

      {isError ? (
        renderErrorState()
      ) : invoices.length === 0 && !isFetching ? (
        renderEmptyState()
      ) : (
        <DataTable
          colDefs={colDefs}
          name="contactInvoices"
          rowConfig={rowConfig}
          rowData={isFetching ? [{ displayAsBlankRow: true }] : invoices}
          sortConfig={sortConfig}
          isLoading={isFetching}
          showBlankRows={isFetching}
          rowCount={isFetching ? 0 : invoices.length}
          autoColumnWidth
        />
      )}
    </div>
  )
}

export default ContactInvoicesTable
